import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { usePractice } from '../context/PracticeContext';

// outbound #445: explain a channel cell in plain words. Uses only fields the
// readiness endpoint already returns (sent / pending / suppressed / cancelled,
// opt-out, rule match) — no extra request.
export function channelTitle(label, n, optOut, hasRuleMatch) {
  const sent = n?.sent || 0, pending = n?.pending || 0, suppressed = n?.suppressed || 0, cancelled = n?.cancelled || 0;
  if (sent > 0 || pending > 0) {
    return `${label}: ${sent} sent, ${pending} pending` + (suppressed ? `, ${suppressed} suppressed` : '') + (cancelled ? `, ${cancelled} cancelled` : '');
  }
  if (optOut) return `${label}: patient opted out — nothing sent`;
  // commons #230: name the actual latest reason when we have it
  if (suppressed > 0 && n?.suppression_reason) return `${label}: ${suppressed} suppressed — latest: ${n.suppression_reason}`;
  if (suppressed > 0) return `${label}: ${suppressed} suppressed (consent, quiet hours, rate limit or master toggle) — check the delivery log`;
  if (cancelled > 0) return `${label}: ${cancelled} cancelled (appointment changed) — nothing outstanding`;
  if (!hasRuleMatch) return `${label}: no notification rule matches this appointment type / provider — nothing was scheduled`;
  return `${label}: rule matched but nothing scheduled yet on this channel`;
}

/**
 * Pre-Visit Operations Readiness dashboard — per-appointment view of
 * "what's about to fall through the cracks before this patient walks in?"
 *
 * Surfaces 6 gap signals per row:
 *   - no_rule_match — no notification rule will fire
 *   - no_contact_info — patient has no phone AND no email
 *   - notification_silent — rule matched but nothing scheduled (config bug)
 *   - all_channels_opt_out — both SMS + email opt out
 *   - no_form_generated — no intake form for this appt
 *   - form_stale_near_appt — form NOT_STARTED + < 48h to appt
 *
 * Backend: GET /practices/:practiceId/operations/readiness
 *          GET /practices/:practiceId/operations/readiness/stats
 *          GET /practices/:practiceId/appointments/:apptId/messages
 */
function OperationsReadiness() {
  const { selectedPracticeId } = usePractice();
  const today = new Date().toISOString().slice(0, 10);
  const sevenAhead = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);

  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(sevenAhead);
  const [providerFilter, setProviderFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('scheduled');
  const [gapOnly, setGapOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedAppt, setExpandedAppt] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState({});
  // Filter dropdown options — loaded once per practice
  const [providerOptions, setProviderOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  // Sort state — clicking a column header toggles asc → desc → off
  const [sortBy, setSortBy] = useState('datetime');
  const [sortDir, setSortDir] = useState('asc');

  // Load filter dropdown options when the practice changes
  useEffect(() => {
    if (!selectedPracticeId) return;
    (async () => {
      try {
        const res = await api.request(`/practices/${selectedPracticeId}/operations/filters`);
        if (res?.success) {
          setProviderOptions(res.data?.providers || []);
          setTypeOptions(res.data?.appointment_types || []);
        }
      } catch { /* silent — filters fall back to free-text */ }
    })();
  }, [selectedPracticeId]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const sortedRows = React.useMemo(() => {
    if (!rows || rows.length === 0) return rows;
    const arr = [...rows];
    const dir = sortDir === 'asc' ? 1 : -1;
    const getKey = {
      datetime: (r) => `${String(r.date || '').slice(0, 10)}T${(r.time || '00:00:00').slice(0, 8)}`,
      type: (r) => (r.appointment_type || '').toLowerCase(),
      patient: (r) => (r.patient?.name || '').toLowerCase(),
      provider: (r) => (r.provider?.name || '').toLowerCase(),
      sms_sent: (r) => r.notifications?.sms?.sent || 0,
      email_sent: (r) => r.notifications?.email?.sent || 0,
      form_pct: (r) => (r.intake_form?.completion_pct ?? -1),
      gap_count: (r) => (r.gaps?.length || 0),
    }[sortBy] || ((r) => 0);
    arr.sort((a, b) => {
      const ka = getKey(a);
      const kb = getKey(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [rows, sortBy, sortDir]);

  const reload = useCallback(async () => {
    if (!selectedPracticeId) return;
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ from, to });
      if (providerFilter) qs.set('provider_id', providerFilter);
      if (typeFilter) qs.set('appointment_type', typeFilter);
      if (statusFilter) qs.set('status', statusFilter);
      if (gapOnly) qs.set('gap_only', 'true');
      const [r, s] = await Promise.all([
        api.request(`/practices/${selectedPracticeId}/operations/readiness?${qs.toString()}`),
        api.request(`/practices/${selectedPracticeId}/operations/readiness/stats?from=${from}&to=${to}`),
      ]);
      setRows(r?.data || []);
      setStats(s?.data || null);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [selectedPracticeId, from, to, providerFilter, typeFilter, statusFilter, gapOnly]);

  useEffect(() => {
    reload();
  }, [reload]);

  const toggleExpand = async (apptId) => {
    if (expandedAppt === apptId) {
      setExpandedAppt(null);
      return;
    }
    setExpandedAppt(apptId);
    if (!expandedMessages[apptId]) {
      try {
        const res = await api.request(`/practices/${selectedPracticeId}/appointments/${apptId}/messages`);
        setExpandedMessages({ ...expandedMessages, [apptId]: res?.data || [] });
      } catch (err) {
        setExpandedMessages({ ...expandedMessages, [apptId]: [{ error: err.message }] });
      }
    }
  };

  if (!selectedPracticeId) {
    return (
      <div style={st.container}>
        <h2 style={st.title}>Pre-Visit Readiness</h2>
        <div style={st.emptyState}>Please select a practice to view readiness.</div>
      </div>
    );
  }

  return (
    <div style={st.container}>
      <div style={st.header}>
        <h2 style={st.title}>Pre-Visit Readiness</h2>
        <p style={st.subtitle}>
          What's about to fall through the cracks before patients walk in. Click any row to see exactly what messages were sent.
        </p>
      </div>

      {stats && (
        <div style={st.statRow}>
          <StatCard label="Total upcoming" value={stats.total} />
          <StatCard label="Appointment confirmed" value={stats.appointment_confirmed ?? 0} tone="ok" />
          <StatCard label="Both channels sent" value={stats.with_both_channels_sent} tone="ok" />
          <StatCard label="Form COMPLETED" value={stats.with_form_completed} tone="ok" />
          <StatCard label="No rule match" value={stats.no_rule_match} tone={stats.no_rule_match > 0 ? 'danger' : 'ok'} />
          <StatCard label="No form generated" value={stats.no_form_generated} tone={stats.no_form_generated > 0 ? 'warn' : 'ok'} />
          <StatCard label="Missing contact" value={stats.missing_contact} tone={stats.missing_contact > 0 ? 'danger' : 'ok'} />
          <StatCard label="Silent (rule but nothing scheduled)" value={stats.notification_silent} tone={stats.notification_silent > 0 ? 'danger' : 'ok'} />
          <StatCard label="All channels opted out" value={stats.all_channels_opt_out} tone={stats.all_channels_opt_out > 0 ? 'warn' : 'ok'} />
        </div>
      )}

      <div style={st.filters}>
        <label style={st.filterField}>
          <span style={st.filterLabel}>From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={st.filterInput} />
        </label>
        <label style={st.filterField}>
          <span style={st.filterLabel}>To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={st.filterInput} />
        </label>
        <label style={st.filterField}>
          <span style={st.filterLabel}>Status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={st.filterInput}>
            <option value="">all</option>
            <option value="scheduled">scheduled</option>
            <option value="confirmed">confirmed</option>
            <option value="canceled">canceled</option>
            <option value="completed">completed</option>
          </select>
        </label>
        <label style={st.filterField}>
          <span style={st.filterLabel}>Appt type</span>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={st.filterInput}>
            <option value="">all types</option>
            {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label style={st.filterField}>
          <span style={st.filterLabel}>Provider</span>
          <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} style={st.filterInput}>
            <option value="">all providers</option>
            {providerOptions.map(p => <option key={p.id} value={p.id}>{p.name || `Provider ${p.id}`}</option>)}
          </select>
        </label>
        <label style={{ ...st.filterField, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={gapOnly} onChange={(e) => setGapOnly(e.target.checked)} />
          <span style={st.filterLabel}>Show only rows with gaps</span>
        </label>
      </div>

      {error && <div style={st.errorBanner}>{error}</div>}

      <DigestPanel practiceId={selectedPracticeId} />

      <div style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>
        Showing {rows.length} row{rows.length === 1 ? '' : 's'} {loading && '(loading…)'}
      </div>

      <table style={st.table}>
        <thead>
          <tr style={st.theadRow}>
            <SortTh col="datetime" sortBy={sortBy} sortDir={sortDir} onClick={handleSort}>Date / Time</SortTh>
            <SortTh col="type" sortBy={sortBy} sortDir={sortDir} onClick={handleSort}>Type</SortTh>
            <SortTh col="patient" sortBy={sortBy} sortDir={sortDir} onClick={handleSort}>Patient</SortTh>
            <SortTh col="provider" sortBy={sortBy} sortDir={sortDir} onClick={handleSort}>Provider</SortTh>
            <th style={st.th} title="Contact details on file for the patient — not delivery status">Contact on file</th>
            {/* GH-200 follow-up: tester asked for a per-row confirmation indicator,
                not just the header count. Dedicated column so it stands out. */}
            <th style={st.th}>Confirmed</th>
            <SortTh col="sms_sent" sortBy={sortBy} sortDir={sortDir} onClick={handleSort}>SMS</SortTh>
            <SortTh col="email_sent" sortBy={sortBy} sortDir={sortDir} onClick={handleSort}>Email</SortTh>
            <SortTh col="form_pct" sortBy={sortBy} sortDir={sortDir} onClick={handleSort}>Form</SortTh>
            <SortTh col="gap_count" sortBy={sortBy} sortDir={sortDir} onClick={handleSort}>Gaps</SortTh>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map(r => (
            <React.Fragment key={r.appointment_id}>
              <tr
                style={{ ...st.tr, background: r.gaps.length > 0 ? '#fef9c3' : 'white', cursor: 'pointer' }}
                onClick={() => toggleExpand(r.appointment_id)}
              >
                <td style={st.td}>
                  <div style={{ fontWeight: 600 }}>{r.date?.slice(0, 10)}</div>
                  <div style={{ color: '#6b7280', fontSize: 12 }}>{(r.time || '').slice(0, 5)}</div>
                </td>
                <td style={st.td}>
                  <div>{r.appointment_type}</div>
                  <div style={{ color: '#6b7280', fontSize: 11 }}>{r.appt_status}</div>
                </td>
                <td style={st.td}>{r.patient?.name}</td>
                <td style={st.td}>{r.provider?.name || '—'}</td>
                {/* outbound #445: these chips mean "contact details ON FILE", not
                    "SMS/email activity" — the tester read 📱 as an SMS indicator next
                    to 0S/0P. Titles + the renamed header make the meaning explicit;
                    delivery activity lives in the SMS / Email columns. */}
                <td style={st.td}>
                  {r.patient?.has_phone && <span style={st.chipOk} title="Mobile number on file (not an SMS status)">📱</span>}
                  {r.patient?.has_email && <span style={st.chipOk} title="Email address on file (not an email status)">✉</span>}
                  {!r.patient?.has_phone && !r.patient?.has_email && <span style={st.chipDanger} title="No phone or email on file">none</span>}
                </td>
                {/* GH-200: per-row confirmation indicator. Reads patient_confirmed
                    (appointments.patient_confirmed_at, migration 163), NOT appt_status —
                    'confirmed' is not a valid appointment status. */}
                <td style={st.td} title={r.patient_confirmed_at ? `Confirmed ${new Date(r.patient_confirmed_at).toLocaleString()}` : undefined}>
                  {r.patient_confirmed
                    ? <span style={{ ...st.chipOk, background: '#dcfce7', color: '#166534' }}>✓ Confirmed</span>
                    : <span style={{ color: '#9ca3af' }}>—</span>}
                </td>
                {/* outbound #445: when nothing was sent or is pending, show "—" with
                    the REASON in the tooltip instead of a grey "0S / 0P" — the tester's
                    real question was "why did this row get an email but no SMS?" */}
                {/* commons #230 (Satish): when a channel was suppressed and nothing went
                    out, say WHY inline — the reason is recorded on the notification. */}
                <td style={st.td} title={channelTitle('SMS', r.notifications.sms, r.consent.sms_opt_out, r.has_rule_match)}>
                  {(r.notifications.sms.sent > 0 || r.notifications.sms.pending > 0)
                    ? <span style={{ color: r.notifications.sms.sent > 0 ? '#065f46' : '#9ca3af' }}>{r.notifications.sms.sent}S / {r.notifications.sms.pending}P</span>
                    : r.notifications.sms.suppressed > 0 && r.notifications.sms.suppression_reason
                      ? <span style={{ color: '#b45309', fontSize: 12 }}>— suppressed: {r.notifications.sms.suppression_reason}</span>
                      : <span style={{ color: '#9ca3af' }}>—</span>}
                  {r.consent.sms_opt_out && <span style={st.chipWarn}>opt-out</span>}
                </td>
                <td style={st.td} title={channelTitle('Email', r.notifications.email, r.consent.email_opt_out, r.has_rule_match)}>
                  {(r.notifications.email.sent > 0 || r.notifications.email.pending > 0)
                    ? <span style={{ color: r.notifications.email.sent > 0 ? '#065f46' : '#9ca3af' }}>{r.notifications.email.sent}S / {r.notifications.email.pending}P</span>
                    : r.notifications.email.suppressed > 0 && r.notifications.email.suppression_reason
                      ? <span style={{ color: '#b45309', fontSize: 12 }}>— suppressed: {r.notifications.email.suppression_reason}</span>
                      : <span style={{ color: '#9ca3af' }}>—</span>}
                  {r.consent.email_opt_out && <span style={st.chipWarn}>opt-out</span>}
                </td>
                <td style={st.td}>
                  {r.intake_form?.id ? (
                    <span style={r.intake_form.status === 'COMPLETED' ? st.chipOk : st.chipWarn}>
                      {r.intake_form.status} {r.intake_form.completion_pct != null ? `${Math.round(r.intake_form.completion_pct)}%` : ''}
                    </span>
                  ) : <span style={st.chipDanger}>no form</span>}
                </td>
                <td style={st.td}>
                  {r.gaps.map(g => <span key={g} style={st.gapChip}>{prettyGap(g)}</span>)}
                </td>
              </tr>
              {expandedAppt === r.appointment_id && (
                <tr>
                  <td colSpan={10} style={st.expandedCell}>
                    <ExpandedMessages
                      messages={expandedMessages[r.appointment_id]}
                      appointmentId={r.appointment_id}
                      practiceId={selectedPracticeId}
                      onReload={reload}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {rows.length === 0 && !loading && (
            <tr><td colSpan={10} style={{ ...st.td, textAlign: 'center', padding: 32, color: '#6b7280' }}>
              No appointments in this window.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DigestPanel({ practiceId }) {
  const [expanded, setExpanded] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [recipientsText, setRecipientsText] = useState('');
  const [sendTime, setSendTime] = useState('07:30');
  const [windowDays, setWindowDays] = useState(7);
  const [isActive, setIsActive] = useState(true);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!practiceId || !expanded) return;
    (async () => {
      try {
        const res = await api.request(`/practices/${practiceId}/operations/digest`);
        setSchedule(res?.data || null);
        if (res?.data) {
          setSendTime(res.data.send_time_local || '07:30');
          setWindowDays(res.data.window_days || 7);
          setIsActive(!!res.data.is_active);
          const emails = Array.isArray(res.data.recipient_emails)
            ? res.data.recipient_emails
            : (typeof res.data.recipient_emails === 'string'
                ? (() => { try { return JSON.parse(res.data.recipient_emails); } catch { return []; } })()
                : []);
          setRecipientsText(emails.join(', '));
        }
      } catch (err) {
        setStatus({ ok: false, msg: err.message });
      }
    })();
  }, [practiceId, expanded]);

  const save = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const recipients = recipientsText.split(',').map(s => s.trim()).filter(Boolean);
      const res = await api.request(`/practices/${practiceId}/operations/digest`, {
        method: 'PUT',
        body: {
          send_time_local: sendTime,
          window_days: parseInt(windowDays, 10) || 7,
          recipient_emails: recipients,
          is_active: isActive ? 1 : 0,
        },
      });
      if (res?.success) {
        setSchedule(res.data);
        setStatus({ ok: true, msg: 'Saved' });
      } else {
        setStatus({ ok: false, msg: res?.error || 'Save failed' });
      }
    } catch (err) {
      setStatus({ ok: false, msg: err.message });
    }
    setLoading(false);
  };

  const sendNow = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const res = await api.request(`/practices/${practiceId}/operations/digest/send-now`, { method: 'POST' });
      if (res?.success) {
        setStatus({
          ok: true,
          msg: `Sent — ${res.data.gapCount} gap${res.data.gapCount === 1 ? '' : 's'} in digest. Delivery: ${res.data.recipients.map(r => `${r.email} ${r.success ? '✓' : '✗'}`).join(', ')}`,
        });
      } else {
        setStatus({ ok: false, msg: res?.error || 'Send failed' });
      }
    } catch (err) {
      setStatus({ ok: false, msg: err.message });
    }
    setLoading(false);
  };

  return (
    <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: 'white' }}>
      <button type="button" style={{ ...st.digestHeader }} onClick={() => setExpanded(!expanded)}>
        <strong>Daily Email Digest</strong>
        <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>
          {schedule ? `${schedule.send_time_local} local · ${(schedule.recipient_emails || []).length || 0} recipients · ${schedule.is_active ? 'active' : 'paused'}` : 'not configured — click to set up'}
        </span>
        <span style={{ marginLeft: 'auto', color: '#6b7280' }}>{expanded ? '▴' : '▾'}</span>
      </button>
      {expanded && (
        <div style={{ padding: 16, borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
            Sends a gap-only readiness summary once daily to the listed recipients. Cron runs every 15 min and fires when the practice's local time matches the configured time.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
            <label style={st.filterField}>
              <span style={st.filterLabel}>Send time (local HH:MM)</span>
              <input type="time" value={sendTime} onChange={(e) => setSendTime(e.target.value)} style={st.filterInput} />
            </label>
            <label style={st.filterField}>
              <span style={st.filterLabel}>Window (days)</span>
              <input type="number" min={1} max={30} value={windowDays} onChange={(e) => setWindowDays(e.target.value)} style={{ ...st.filterInput, width: 80 }} />
            </label>
            <label style={{ ...st.filterField, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <span style={st.filterLabel}>Active</span>
            </label>
          </div>
          <label style={st.filterField}>
            <span style={st.filterLabel}>Recipients (comma-separated emails)</span>
            <input type="text" value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} style={{ ...st.filterInput, width: 500, maxWidth: '100%' }} placeholder="ops@practice.com, manager@practice.com" />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={save} disabled={loading} style={{ ...st.actionBtn, background: '#4f46e5', color: 'white', borderColor: '#4f46e5' }}>
              {loading ? '…' : 'Save schedule'}
            </button>
            <button type="button" onClick={sendNow} disabled={loading} style={st.actionBtn}>
              Send digest now
            </button>
          </div>
          {status && (
            <div style={{
              padding: 8, borderRadius: 4, fontSize: 12,
              color: status.ok ? '#065f46' : '#991b1b',
              background: status.ok ? '#ecfdf5' : '#fee2e2',
            }}>
              {status.msg}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SortTh({ col, sortBy, sortDir, onClick, children }) {
  const active = sortBy === col;
  const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th
      style={{
        ...st.th,
        cursor: 'pointer',
        userSelect: 'none',
        color: active ? '#4f46e5' : '#374151',
      }}
      onClick={() => onClick(col)}
      title="Click to sort"
    >
      {children}{arrow}
    </th>
  );
}

function StatCard({ label, value, tone = 'neutral' }) {
  const bg = tone === 'ok' ? '#ecfdf5' : tone === 'warn' ? '#fef3c7' : tone === 'danger' ? '#fee2e2' : '#f3f4f6';
  const color = tone === 'ok' ? '#065f46' : tone === 'warn' ? '#92400e' : tone === 'danger' ? '#991b1b' : '#374151';
  return (
    <div style={{ ...st.statCard, background: bg, color }}>
      <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function ExpandedMessages({ messages, appointmentId, practiceId, onReload }) {
  const [actionStatus, setActionStatus] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplateId, setSmsTemplateId] = useState('');
  const [emailTemplateId, setEmailTemplateId] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.getNotificationTemplates(practiceId, 'sms').then(res => { if (!cancelled) setSmsTemplates(res?.data || []); }).catch(() => {});
    api.getNotificationTemplates(practiceId, 'email').then(res => { if (!cancelled) setEmailTemplates(res?.data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [practiceId]);

  const runAction = async (endpoint, label) => {
    setActionLoading(true);
    setActionStatus(null);
    try {
      const res = await api.request(endpoint, { method: 'POST' });
      if (res?.success) {
        setActionStatus({ ok: true, msg: `${label} OK: ${JSON.stringify(res.data)}` });
        if (onReload) await onReload();
      } else {
        setActionStatus({ ok: false, msg: res?.error || 'Failed' });
      }
    } catch (err) {
      setActionStatus({ ok: false, msg: err.message });
    }
    setActionLoading(false);
  };

  const sendManual = async (channel, templateId) => {
    if (!templateId) { setActionStatus({ ok: false, msg: `Pick a ${channel.toUpperCase()} template first` }); return; }
    setActionLoading(true);
    setActionStatus(null);
    try {
      const res = await api.sendNotificationNow(practiceId, {
        appointment_id: appointmentId,
        channel,
        template_id: parseInt(templateId),
      });
      const status = res?.data?.result?.status || (res?.success ? 'SENT' : 'FAILED');
      setActionStatus({ ok: res?.success, msg: `${channel.toUpperCase()} manual send: ${status}` });
      if (onReload) await onReload();
    } catch (err) {
      setActionStatus({ ok: false, msg: err.message });
    }
    setActionLoading(false);
  };

  return (
    <div style={{ padding: 16, background: '#f9fafb' }}>
      {/* Row actions */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={actionLoading}
          onClick={() => runAction(`/practices/${practiceId}/appointments/${appointmentId}/notifications/reevaluate`, 'Re-evaluate rules')}
          style={st.actionBtn}>
          {actionLoading ? '…' : 'Re-evaluate notification rules'}
        </button>
        <button
          type="button"
          disabled={actionLoading}
          onClick={() => {
            if (!window.confirm('Regenerate intake form?\n\nThis will EXPIRE the existing form and create a fresh one from the current patient and practice data. Any in-progress patient answers will be lost.\n\nProceed?')) return;
            runAction(`/practices/${practiceId}/appointments/${appointmentId}/intake-form/regenerate`, 'Regenerate form');
          }}
          style={st.actionBtn}>
          {actionLoading ? '…' : 'Generate / regenerate intake form'}
        </button>
        <a
          href={`/practice-view/care-hub?appointment_id=${appointmentId}`}
          style={{ ...st.actionBtn, textDecoration: 'none', display: 'inline-block', textAlign: 'center' }}
        >
          Open in Care Hub
        </a>
      </div>

      {/* GH-230: per-patient manual notification triggers (paperwork / meeting link) */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={smsTemplateId}
          onChange={e => setSmsTemplateId(e.target.value)}
          disabled={actionLoading || smsTemplates.length === 0}
          style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4, border: '1px solid #d1d5db' }}
          title="Pick an SMS template (paperwork reminder, meeting link, etc.)"
        >
          <option value="">— SMS template —</option>
          {smsTemplates.map(t => <option key={t.id} value={t.id}>{t.name || t.template_name}</option>)}
        </select>
        <button type="button" disabled={actionLoading} onClick={() => sendManual('sms', smsTemplateId)} style={st.actionBtn}>
          Trigger SMS
        </button>
        <select
          value={emailTemplateId}
          onChange={e => setEmailTemplateId(e.target.value)}
          disabled={actionLoading || emailTemplates.length === 0}
          style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4, border: '1px solid #d1d5db' }}
          title="Pick an Email template (paperwork reminder, meeting link, etc.)"
        >
          <option value="">— Email template —</option>
          {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.name || t.template_name}</option>)}
        </select>
        <button type="button" disabled={actionLoading} onClick={() => sendManual('email', emailTemplateId)} style={st.actionBtn}>
          Trigger Email
        </button>
      </div>
      {actionStatus && (
        <div style={{
          padding: 8, marginBottom: 12, borderRadius: 4, fontSize: 12,
          color: actionStatus.ok ? '#065f46' : '#991b1b',
          background: actionStatus.ok ? '#ecfdf5' : '#fee2e2',
        }}>
          {actionStatus.msg}
        </div>
      )}

      {/* Sent messages */}
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        Messages sent ({messages?.length || 0})
      </div>
      {!messages ? <div style={{ color: '#6b7280' }}>Loading messages...</div>
        : messages.length === 0 ? <div style={{ color: '#6b7280' }}>No messages sent for this appointment yet.</div>
        : messages[0]?.error ? <div style={{ color: '#991b1b' }}>Error: {messages[0].error}</div>
        : messages.map(m => (
          <div key={m.id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, marginBottom: 8, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <strong>{m.channel?.toUpperCase()}</strong>
              <span style={{ color: '#6b7280', fontSize: 11 }}>{m.action_type} · {m.created_at?.slice(0, 16)}</span>
            </div>
            {m.subject && <div style={{ fontWeight: 500, marginBottom: 4 }}>Subject: {m.subject}</div>}
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', color: '#374151' }}>{m.body || '(no body persisted)'}</pre>
          </div>
        ))
      }
    </div>
  );
}

function prettyGap(g) {
  const labels = {
    no_rule_match: 'No rule match',
    no_contact_info: 'No contact info',
    notification_silent: 'No notifs scheduled',
    all_channels_opt_out: 'Opt-out both',
    no_form_generated: 'No form',
    form_stale_near_appt: 'Form NOT_STARTED <48h',
  };
  return labels[g] || g;
}

const st = {
  container: { padding: 16, maxWidth: 1600, margin: '0 auto' },
  header: { marginBottom: 12 },
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  emptyState: { padding: 24, color: '#6b7280' },
  statRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 16 },
  statCard: { padding: 12, borderRadius: 8 },
  filters: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end', padding: 12, background: '#f9fafb', borderRadius: 8 },
  filterField: { display: 'flex', flexDirection: 'column', gap: 4 },
  filterLabel: { fontSize: 11, fontWeight: 500, color: '#6b7280', textTransform: 'uppercase' },
  filterInput: { padding: '6px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6 },
  errorBanner: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 6, marginTop: 12 },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: 13, background: 'white' },
  theadRow: { background: '#f9fafb' },
  th: { textAlign: 'left', padding: '10px 8px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: 12 },
  tr: { borderBottom: '1px solid #e5e7eb' },
  td: { padding: '10px 8px', verticalAlign: 'top' },
  chipOk: { display: 'inline-block', padding: '2px 6px', fontSize: 11, fontWeight: 600, color: '#065f46', background: '#ecfdf5', borderRadius: 4, marginRight: 4 },
  chipWarn: { display: 'inline-block', padding: '2px 6px', fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', borderRadius: 4, marginLeft: 4 },
  chipDanger: { display: 'inline-block', padding: '2px 6px', fontSize: 11, fontWeight: 600, color: '#991b1b', background: '#fee2e2', borderRadius: 4 },
  gapChip: { display: 'inline-block', padding: '2px 6px', fontSize: 11, fontWeight: 500, color: '#92400e', background: '#fef3c7', borderRadius: 4, marginRight: 4, marginBottom: 2 },
  expandedCell: { padding: 0, background: '#f9fafb' },
  actionBtn: { padding: '6px 12px', fontSize: 12, fontWeight: 500, color: '#374151', background: 'white', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' },
  digestHeader: { width: '100%', padding: '12px 16px', fontSize: 14, textAlign: 'left', background: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' },
};

export default OperationsReadiness;
